/*
 * Product shapes as the storefront API returns them (`/products`,
 * `/products/:id`, `/products/:id/offers`, `/products/filters`).
 *
 * One definition for the card row, the detail page, an offer and a facet.
 * Views and components import from here and never redeclare these
 * interfaces; if the API gains a field, it is added once, here.
 */

/** Row shape of `GET /products` — what a card, rail or search hit needs. */
export interface ProductSummary {
  id: string;
  name: string;
  platform: string | null;
  ourPriceCents: number;
  /** Not in the catalog yet; a card shows a discount badge only when present. */
  msrpCents?: number | null;
  totalQty: number;
  imageUrl: string | null;
  coverUrl: string | null;
  isPreorder: boolean;
  genres?: string[] | null;
  includesDlc?: boolean | null;
  /** Live offers at last sync — the honest availability signal. */
  offerCount?: number | null;
  available?: boolean;
  releaseDate?: string | null;
  ageRating?: number | string | null;
  metacriticScore?: number | null;
}

/** `GET /products/:id`. */
export interface ProductDetail extends ProductSummary {
  kinguinId: number;
  description: string | null;
  genres: string[];
  developers: string[];
  publishers: string[];
  releaseDate: string | null;
  textQty: number;
  languages: string[];
  regions?: string[];
  region?: string | null;
  screenshots?: unknown;
  available: boolean;
  offerCount: number;
}

/** One seller offer from `GET /products/:id/offers`. */
export interface ProductOffer {
  id: string;
  kinguinOfferId: string;
  priceCents: number;
  ourPriceCents: number;
  merchantName: string | null;
  keyType: string | null;
  qty: number;
  available: boolean;
}

export interface Facet {
  name: string;
  count: number;
}

export interface Paged<T> {
  items: T[];
  total: number;
}

/*
 * Kinguin ships a grey crown `_placeholder.png` for products without art.
 * Treat it as "no art" so tiles fall back to the branded placeholder instead
 * of adopting someone else's.
 */
function realArt(url: string | null | undefined): string | null {
  return url && !url.includes('_placeholder') ? url : null;
}

/** Best art for a tile: the portrait cover, else the landscape key art. */
export function productCover(p: Pick<ProductSummary, 'coverUrl' | 'imageUrl'>): string | null {
  return realArt(p.coverUrl) ?? realArt(p.imageUrl);
}

/** Landscape art first — hero, quick view, lightbox. */
export function productArt(p: Pick<ProductSummary, 'coverUrl' | 'imageUrl'>): string | null {
  return realArt(p.imageUrl) ?? realArt(p.coverUrl);
}

/*
 * The one definition of "buyable" for list rows: live, and something on
 * offer. `offerCount` wins over `totalQty` because the two disagree briefly
 * after a catalog sync; the product page re-checks against live offers.
 */
export function isPurchasable(p: Pick<ProductSummary, 'available' | 'offerCount' | 'totalQty'>): boolean {
  return (p.available ?? true) && (p.offerCount ?? p.totalQty) > 0;
}
